import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { AsyncPipe, KeyValuePipe, NgClass } from "@angular/common";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { MatDivider } from "@angular/material/divider";
import { BetPlacementService } from "../../../../../../shared/src/lib/services/bet-placement.service";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { SnackbarService } from "../../../../../../shared/src/lib/services/snackbar.service";
import { ApiService } from "../../../../../../shared/src";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { quickBetAmountsValidator, quickBetAmountValidator } from "../../../../../../shared/src/lib/utils/common";
import { MatButton } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { LiveUpdateService } from "../../../../../../shared/src/lib/services/live-update.service";
import { Observable, Subject, Subscription } from "rxjs";
import { BpOverlayComponent } from "../../../components/bp-overlay/bp-overlay.component";
import { WhiteLabelConfigService } from "../../../../../../shared/src/lib/services/white-label-config.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-bet-slip",
  imports: [NgClass, MatRadioButton, MatDivider, MatRadioGroup, ReactiveFormsModule, MatButton, MatIconModule, KeyValuePipe, AsyncPipe, BpOverlayComponent],
  templateUrl: "./bet-slip.component.html",
  styleUrl: "./bet-slip.component.scss",
})
export class BetSlipComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  liveUpdateService = inject(LiveUpdateService);
  placementService = inject(BetPlacementService);
  apiService = inject(ApiService);
  snackbar = inject(SnackbarService);
  loader = inject(LoaderService);
  isEditingStake = false;
  slipSettings = [
    {
      accessor: "flashOnPriceChange",
      title: "Flash When Prices Change",
      options: [
        { label: "Enable", value: "enable" },
        { label: "Disable", value: "disable" },
      ],
    },
    {
      accessor: "persistenceType",
      title: "Persistence Type",
      options: [
        { label: "Keep", value: "PERSIST" },
        { label: "Cancel", value: "LAPSE" },
      ],
    },
    {
      accessor: "appearance",
      title: "Betslip Appearance",
      options: [
        { label: "Traditional Betslip", value: "traditional" },
        { label: "One-Click-Betting Betslip", value: "oneClickBetting" },
      ],
    },
  ];
  form: FormGroup;
  betSlipSettings: any = null;
  wlSlipSettings: any;
  openBetsLoading$!: Observable<boolean>;
  wlConfigService = inject(WhiteLabelConfigService);

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      appearance: [""],
      cashOut: [""],
      defaultStake: [""],
      flashOnPriceChange: [""],
      persistenceType: ["PERSIST"],
      whatIfFigure: [""],
      quickBetAmounts: this.fb.array([], quickBetAmountsValidator()), // an array of strings
    });
  }

  ngOnInit(): void {
    this.openBetsLoading$ = this.loader.isLoading("/bf/current-user-order");
    this.wlSlipSettings = this.placementService.getWLSlipSettings();
    // console.log(this.wlSlipSettings);
    this.setDefaultValue();

    this.placementService.changeNotifier$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      if (type === "slipChange") {
        this.wlSlipSettings = this.placementService.getWLSlipSettings();
        this.cdr.detectChanges();
      }
    });

    this.appearance?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value === "oneClickBetting") {
        this.defaultStake?.setValidators(Validators.required);
      } else {
        this.defaultStake?.removeValidators(Validators.required);
      }
      this.defaultStake?.updateValueAndValidity();
    });

    this.quickBetAmounts.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (!value.includes(this.betSlipSettings?.defaultStake)) {
        this.defaultStake?.setValue("");
      } else if (value.includes(this.betSlipSettings?.defaultStake) && !this.defaultStake?.value) {
        this.defaultStake?.setValue(this.betSlipSettings?.defaultStake);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get quickBetAmounts(): FormArray {
    return this.form.get("quickBetAmounts") as FormArray;
  }

  get flashOnPriceChange() {
    return this.form.get("flashOnPriceChange");
  }

  get persistenceType() {
    return this.form.get("persistenceType");
  }

  get appearance() {
    return this.form.get("appearance");
  }

  get defaultStake() {
    return this.form.get("defaultStake");
  }

  addQuickBetAmount(value: number) {
    this.quickBetAmounts.push(this.fb.control(value, [quickBetAmountValidator()])); // Add new control
  }

  setDefaultValue() {
    const userSlipSettings = this.placementService.getSlipSettings();
    let betSlipSettings = userSlipSettings?.betSlipSettings ?? this.wlSlipSettings?.betSlipSettings;
    let quickBetAmounts: any[] = userSlipSettings?.quickBetAmounts?.length > 0 ? userSlipSettings?.quickBetAmounts : this.wlSlipSettings?.quickBetAmounts;
    betSlipSettings = {
      ...betSlipSettings,
      appearance: betSlipSettings?.appearance ?? "traditional",
      flashOnPriceChange: betSlipSettings?.flashOnPriceChange ?? "enable",
      persistenceType: betSlipSettings?.persistenceType ?? "PERSIST",
    };
    if (betSlipSettings?.appearance === "oneClickBetting") this.defaultStake?.setValidators(Validators.required);
    this.form.patchValue(betSlipSettings);
    quickBetAmounts?.forEach((value: number) => {
      this.addQuickBetAmount(value);
    });
    this.betSlipSettings = betSlipSettings;
  }

  handleSubmit() {
    if (this.form.invalid) {
      this.snackbar.openSnackbar({ message: "Form is invalid." });
      return;
    }

    const app = this.apiService.getUserToken();
    const ID = app?.user?.ID;
    const { quickBetAmounts, ...rest } = this.form.value;
    const data = {
      ID,
      BETSLIP: {
        betSlipSettings: {
          ...rest,
          whatIfFigure: "disable",
        },
        quickBetAmounts,
      },
    };
    // console.log(data);
    this.placementService
      .updateBetSlipSettings(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.status === "ok") {
            this.snackbar.openSnackbar({
              message: "Bet Slip Settings Updated Successfully!",
            });

            let app = this.apiService.getUserToken();
            app = {
              ...app,
              user: {
                ...app?.user,
                USER_BETSLIP: JSON.stringify(data?.BETSLIP),
              },
            };

            this.apiService.saveUserToken(app);
            this.placementService.notifyChange("slipChange");
          }
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  handleDefaultStake(event: any) {
    this.form.patchValue({
      defaultStake: event.checked ? this.betSlipSettings?.defaultStake : null,
    });
  }

  setDefaultStake(value: number) {
    this.form.patchValue({ defaultStake: value });
    this.betSlipSettings = { ...this.betSlipSettings, defaultStake: value };
  }
}
